import { Component, inject, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UploadConfigService } from '../../../shared/services/upload-config.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';
import { BatchUploadService } from '../../services/batch-upload.service';

@Component({
  selector: 'app-bulk-brochure-analysis',
  standalone: true,
  imports: [ButtonModule, ProgressBarModule, TranslocoModule],
  templateUrl: './bulk-brochure-analysis.component.html',
})
export class BulkBrochureAnalysisComponent {
  protected readonly batchUploadService = inject(BatchUploadService);
  private notificationService = inject(NotificationService);
  private transloco = inject(TranslocoService);
  private uploadConfig = inject(UploadConfigService);
  private packLimitationService = inject(PackLimitationsService);

  closed = output<void>();

  draggedFileIndex: number | null = null;

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const { duplicates, tooBig } = this.batchUploadService.addFiles(
      Array.from(input.files),
    );

    if (duplicates.length > 0) {
      this.notificationService.error(
        this.transloco.translate('calculator.duplicateFilesDetail', {
          count: duplicates.length,
          files: duplicates.join(', '),
        }),
      );
    }
    if (tooBig.length > 0) {
      this.notificationService.error(
        this.transloco.translate('calculator.filesTooBigError', {
          count: tooBig.length,
          maxSize: this.uploadConfig.AI_DOCUMENT.maxSizeMB,
          files: tooBig.join(', '),
        }),
      );
    }
    // Reset input to allow re-selecting the same files
    input.value = '';
  }

  // ── Drag & drop: DOM plumbing only; reordering itself lives in the service ──
  onDragStart(event: DragEvent, index: number): void {
    this.draggedFileIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    if (this.draggedFileIndex === null) return;
    this.batchUploadService.reorderFile(this.draggedFileIndex, dropIndex);
    this.draggedFileIndex = null;
  }

  onDropAtEnd(event: DragEvent): void {
    event.preventDefault();
    if (this.draggedFileIndex === null) return;
    this.batchUploadService.reorderFile(
      this.draggedFileIndex,
      this.batchUploadService.files().length,
    );
    this.draggedFileIndex = null;
  }

  onDragEnd(): void {
    this.draggedFileIndex = null;
  }

  cancel(): void {
    this.batchUploadService.reset();
    this.closed.emit();
  }

  startAnalysis(): void {
    if (this.batchUploadService.filesToUpload().length === 0) return;

    // Kick off the upload but keep the modal open: it switches to the progress
    // view (driven by `state`) and then to a terminal "done"/"error" view. The
    // user can close it at any time to minimize to the floating widget.
    void this.batchUploadService.runUpload();
  }

  /** Closes the modal from the terminal view; reset is done by onBulkModalClose. */
  close(): void {
    this.closed.emit();
  }

  openModal() {
    return this.packLimitationService.openModal(LimitedFeatures.Trip);
  }
}
